import { GenderDisplay } from '../enums/Genders.enum';
import { Patient } from '../types/Patient.type';

/**
 * User-macro placeholders.
 *
 * A macro's output is authored in a rich-text editor where a placeholder is an
 * atom node rendered as a chip (`<span data-type="macroPlaceholder"
 * data-id="patientFirstName">{patientFirstName}</span>`), showing the same
 * token spelling notifications and campaigns use; the label ("Patient First
 * Name") appears only in the picker lists. When the macro is inserted
 * somewhere, each chip is replaced with the patient's current value as plain
 * text, or with its token (`{patientFirstName}`) when there is no patient
 * context or the patient has no value for it.
 *
 * Bare `{patientFirstName}` tokens typed into a legacy plain-text macro resolve
 * the same way, so a macro written before chips existed still fills in.
 */

/** Tiptap node name for the placeholder chip; also the `data-type` attribute. */
export const MACRO_PLACEHOLDER_NODE = 'macroPlaceholder';

export const MACRO_PLACEHOLDER_IDS = [
  'patientFirstName',
  'patientLastName',
  'patientPreferredName',
  'patientDOB',
  'patientSex',
  'heSheThey',
  'himHerThem',
  'hisHersTheirs',
  'hisHerTheir',
  'primaryProvider',
  'primaryHealthcareProvider',
] as const;

export type MacroPlaceholderId = (typeof MACRO_PLACEHOLDER_IDS)[number];

export type MacroPlaceholder = {
  id: MacroPlaceholderId;
  label: string;
};

export const MACRO_PLACEHOLDERS: MacroPlaceholder[] = [
  { id: 'patientFirstName', label: 'Patient First Name' },
  { id: 'patientLastName', label: 'Patient Last Name' },
  { id: 'patientPreferredName', label: 'Patient Preferred Name' },
  { id: 'patientDOB', label: 'Patient DOB' },
  { id: 'patientSex', label: 'Patient Sex' },
  { id: 'heSheThey', label: 'He/She/They' },
  { id: 'himHerThem', label: 'Him/Her/Them' },
  { id: 'hisHersTheirs', label: 'His/Hers/Theirs' },
  { id: 'hisHerTheir', label: 'His/Her/Their' },
  { id: 'primaryProvider', label: 'Primary Clinician' },
  { id: 'primaryHealthcareProvider', label: 'Primary Healthcare Provider' },
];

export const isMacroPlaceholderId = (id: string): id is MacroPlaceholderId =>
  (MACRO_PLACEHOLDER_IDS as readonly string[]).includes(id);

export const macroPlaceholderLabel = (id: string): string =>
  MACRO_PLACEHOLDERS.find(p => p.id === id)?.label ?? id;

/** The text a placeholder falls back to when it cannot be filled in. */
export const macroPlaceholderToken = (id: string): string => `{${id}}`;

/** Patient fields a placeholder can read. `dateOfBirth` is already the
 *  display string (`MM/DD/YYYY`) by the time the patient reaches the client.
 *  Every field may be null so a raw database row can be passed as-is. */
export type MacroPlaceholderPatient = {
  [K in
    | 'ID'
    | 'fname'
    | 'lname'
    | 'pname'
    | 'dateOfBirth'
    | 'gender'
    | 'pronouns'
    | 'otherPronouns'
    | 'primaryClinician'
    | 'primaryProviderText']?: Patient[K] | null;
};

export type MacroPlaceholderContext = {
  patient?: MacroPlaceholderPatient | null;
  /** Clinic clinicians, used to turn `Patient.primaryClinician` (a user ID) into a name. */
  clinicians?: { ID: string; name: string }[] | null;
};

type PronounForms = {
  subject: string | null;
  object: string | null;
  possessive: string | null;
  determiner: string | null;
};

const DETERMINER_BY_POSSESSIVE: Record<string, string> = {
  his: 'His',
  hers: 'Her',
  theirs: 'Their',
};

/**
 * Splits a pronoun set such as `He/Him/His` or `Ze/Hir/Hirs` into its forms.
 * The patient record stores the set as one string; `Other` defers to the
 * free-text `otherPronouns`. Anything that is not at least `subject/object`
 * is treated as unparseable and the pronoun placeholders keep their tokens.
 */
const parsePronouns = (pronouns?: string | null, otherPronouns?: string | null) => {
  const raw = (pronouns === 'Other' ? otherPronouns : pronouns)?.trim();

  if (!raw) {
    return null;
  }

  const parts = raw
    .split('/')
    .map(part => part.trim())
    .filter(Boolean);

  if (parts.length < 2) {
    return null;
  }

  const [subject, object, possessive = null] = parts;
  let determiner: string | null = null;

  if (possessive) {
    const known = DETERMINER_BY_POSSESSIVE[possessive.toLowerCase()];

    if (known) {
      determiner = known;
    } else if (/s$/i.test(possessive) && possessive.length > 2) {
      // Hirs → Hir, Xyrs → Xyr, Eirs → Eir, Pers → Per, Vers → Ver.
      determiner = possessive.slice(0, -1);
    } else {
      determiner = possessive;
    }
  }

  return { subject, object, possessive, determiner } as PronounForms;
};

const pronounsFromSex = (gender?: string | null): PronounForms | null => {
  if (gender === 'male') {
    return { subject: 'He', object: 'Him', possessive: 'His', determiner: 'His' };
  }

  if (gender === 'female') {
    return { subject: 'She', object: 'Her', possessive: 'Hers', determiner: 'Her' };
  }

  return null;
};

const resolvePronouns = (patient: MacroPlaceholderPatient): PronounForms | null =>
  parsePronouns(patient.pronouns, patient.otherPronouns) || pronounsFromSex(patient.gender);

const nonEmpty = (value?: string | null): string | null => {
  const trimmed = value?.trim();

  return trimmed ? trimmed : null;
};

/**
 * The patient's value for one placeholder, or `null` when it should stay a
 * token: no patient context, or the patient has no value for it.
 */
export const resolveMacroPlaceholder = (
  id: string,
  context?: MacroPlaceholderContext | null
): string | null => {
  const patient = context?.patient;

  if (!patient || !isMacroPlaceholderId(id)) {
    return null;
  }

  switch (id) {
    case 'patientFirstName':
      return nonEmpty(patient.fname);
    case 'patientLastName':
      return nonEmpty(patient.lname);
    case 'patientPreferredName':
      return nonEmpty(patient.pname);
    case 'patientDOB':
      return nonEmpty(patient.dateOfBirth);
    case 'patientSex':
      return patient.gender
        ? (GenderDisplay[patient.gender as keyof typeof GenderDisplay] ?? null)
        : null;
    case 'heSheThey':
      return resolvePronouns(patient)?.subject ?? null;
    case 'himHerThem':
      return resolvePronouns(patient)?.object ?? null;
    case 'hisHersTheirs':
      return resolvePronouns(patient)?.possessive ?? null;
    case 'hisHerTheir':
      return resolvePronouns(patient)?.determiner ?? null;
    case 'primaryProvider': {
      const clinicianId = nonEmpty(patient.primaryClinician);

      if (!clinicianId) {
        return null;
      }

      return nonEmpty(context?.clinicians?.find(c => c.ID === clinicianId)?.name);
    }
    case 'primaryHealthcareProvider':
      return nonEmpty(patient.primaryProviderText);
    default:
      return null;
  }
};

/** A macro phrase authored in the rich-text editor is stored as HTML; macros
 *  created before that are plain text. Callers branch on this to decide how to
 *  insert the phrase into a field. */
export const isHtmlMacroPhrase = (phrase?: string | null): boolean =>
  !!phrase && /<\/?[a-z][^>]*>/i.test(phrase);

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const CHIP_PATTERN = new RegExp(
  `<span\\b([^>]*\\bdata-type="${MACRO_PLACEHOLDER_NODE}"[^>]*)>[\\s\\S]*?<\\/span>`,
  'gi'
);

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

/**
 * Fills bare `{patientFirstName}`-style tokens in text for the given patient.
 * Tokens the patient has no value for stay as typed, and without a patient the
 * text is returned unchanged. Values are inserted verbatim unless `escape` is
 * set, for text that is HTML. Used for macro phrases and for campaign messages,
 * so a token fills the same way everywhere.
 */
export const resolveMacroTokens = (
  text: string,
  context?: MacroPlaceholderContext | null,
  escape = false
): string => {
  if (!text || !context?.patient) {
    return text;
  }

  let result = text;

  MACRO_PLACEHOLDER_IDS.forEach(id => {
    const value = resolveMacroPlaceholder(id, context);

    if (value !== null) {
      result = result.replace(
        new RegExp(escapeRegExp(macroPlaceholderToken(id)), 'g'),
        escape ? escapeHtml(value) : value
      );
    }
  });

  return result;
};

/**
 * Fills every placeholder in a macro phrase for the given patient. Chips and
 * bare tokens the patient has no value for become their token text; with no
 * patient context, every chip becomes its token and bare tokens are left alone.
 * The result is in the same format as the input (HTML stays HTML).
 */
export const resolveMacroPhrase = (
  phrase: string,
  context?: MacroPlaceholderContext | null
): string => {
  if (!phrase) {
    return '';
  }

  const html = isHtmlMacroPhrase(phrase);
  const valueFor = (id: string): string => {
    const value = resolveMacroPlaceholder(id, context);

    if (value === null) {
      return macroPlaceholderToken(id);
    }

    return html ? escapeHtml(value) : value;
  };

  let result = phrase;

  if (html) {
    result = result.replace(CHIP_PATTERN, (match, attrs: string) => {
      const id = attrs.match(/\bdata-id="([^"]*)"/)?.[1];

      return id ? valueFor(id) : match;
    });
  }

  return resolveMacroTokens(result, context, html);
};

const decodeEntities = (value: string): string =>
  value
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&amp;/g, '&');

type ListFrame = { ordered: boolean; count: number };

/**
 * Renders a macro's HTML output as plain text for fields that cannot hold
 * formatting: paragraphs and `<br>` become line breaks, bullet items get `• `
 * and numbered items `1. ` (indented per nesting level), blockquote lines get
 * `> `. Inline marks are dropped. A plain-text phrase is returned unchanged.
 */
export const macroPhraseToPlainText = (phrase: string): string => {
  if (!isHtmlMacroPhrase(phrase)) {
    return phrase;
  }

  const lists: ListFrame[] = [];
  let quoteDepth = 0;
  let out = '';
  // Where the current paragraph's text starts, so an empty `<p></p>` (Tiptap's
  // blank line) still produces a blank line instead of collapsing away.
  let paragraphStart = -1;

  const endsWithNewline = () => out === '' || out.endsWith('\n');
  const linePrefix = () => (quoteDepth > 0 ? '> '.repeat(quoteDepth) : '');
  const breakLine = () => {
    if (!endsWithNewline()) {
      out += '\n';
    }
  };

  const tokens = phrase.split(/(<[^>]+>)/);

  tokens.forEach(token => {
    if (!token) {
      return;
    }

    if (!token.startsWith('<')) {
      out += decodeEntities(token.replace(/\r?\n/g, ' '));

      return;
    }

    const tag = token.match(/^<\/?\s*([a-z0-9]+)/i)?.[1]?.toLowerCase();
    const closing = /^<\//.test(token);

    if (!tag) {
      return;
    }

    switch (tag) {
      case 'br':
        out += '\n';
        break;
      case 'p':
      case 'h1':
      case 'h2':
      case 'h3':
      case 'h4':
      case 'h5':
      case 'h6':
      case 'div':
        if (closing) {
          if (paragraphStart === out.length) {
            out += '\n';
          }

          paragraphStart = -1;
          breakLine();
        } else if (!lists.length) {
          // A paragraph that is not a list item's body starts its own line.
          breakLine();
          out += linePrefix();
          paragraphStart = out.length;
        }
        break;
      case 'ul':
      case 'ol':
        if (closing) {
          lists.pop();
          breakLine();
        } else {
          const start = parseInt(token.match(/\bstart="?(\d+)/i)?.[1] ?? '1', 10);
          lists.push({ ordered: tag === 'ol', count: (Number.isNaN(start) ? 1 : start) - 1 });
        }
        break;
      case 'li':
        if (closing) {
          breakLine();
        } else {
          breakLine();
          const frame = lists[lists.length - 1];
          const indent = '  '.repeat(Math.max(0, lists.length - 1));

          if (frame?.ordered) {
            frame.count += 1;
            out += `${linePrefix()}${indent}${frame.count}. `;
          } else {
            out += `${linePrefix()}${indent}• `;
          }
        }
        break;
      case 'blockquote':
        if (closing) {
          quoteDepth = Math.max(0, quoteDepth - 1);
          breakLine();
        } else {
          breakLine();
          quoteDepth += 1;
        }
        break;
      case 'hr':
        breakLine();
        break;
      default:
        // Inline marks (strong, em, u, s, a, span, code) contribute no text of their own.
        break;
    }
  });

  return out
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/** Macros are listed and offered alphabetically by shortcut everywhere. */
export const sortMacrosByShortcut = <T extends { shortcut?: string | null }>(macros: T[]): T[] =>
  [...macros].sort((a, b) =>
    (a.shortcut ?? '').localeCompare(b.shortcut ?? '', undefined, { sensitivity: 'base' })
  );
